import { useState, useCallback, useMemo, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
// eslint-disable-next-line no-unused-vars
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride'

/**
 * Enhanced Tutorial component using react-joyride with advanced features.
 * 
 * Features:
 * - Custom red beacon component for better visibility
 * - Progress indicator and step counter
 * - Improved styling with dark/light mode support
 * - Better error handling and state management
 * - Responsive design considerations
 * - Accessibility improvements
 * - Interactive tab switching and navigation
 * - Programmatic control of page elements
 *
 * Props:
 * - run: boolean to start/stop the tour
 * - onFinish: callback invoked when tour is finished or skipped
 */

// Custom beacon component with red styling for better visibility
const CustomBeacon = forwardRef(({ size, ...props }, ref) => {
  // Filter out non-DOM props that shouldn't be passed to the button element
  // eslint-disable-next-line no-unused-vars
  const { continuous, isLastStep, onClickOrHover, beaconComponent, tooltipComponent, ...buttonProps } = props;

  return (
    <button
      ref={ref}
      {...buttonProps}
      className="joyride-beacon"
      style={
        {
          animation: 'joyride-beacon-pulse 2s infinite',
          backgroundColor: '#dc2626',
          border: '3px solid rgba(220, 38, 38, 0.3)',
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'inline-block',
          height: size || 36,
          position: 'relative',
          width: size || 36,
          zIndex: 9999,
        }
      }
    >
      <span
        style={{
          animation: 'joyride-beacon-inner 1.5s infinite',
          backgroundColor: '#dc2626',
          borderRadius: '50%',
          display: 'block',
          height: '50%',
          left: '50%',
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50%',
        }}
      />
    </button>
  )
})

// Set display name for forwardRef component
CustomBeacon.displayName = 'CustomBeacon'

// Custom tooltip component with enhanced styling
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  size,
  isLastStep,
}) => {
  return (
    <div
      className="joyride-tooltip"
      style={{
        backgroundColor: 'white',
        borderRadius: 12,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        color: '#374151',
        fontSize: 14,
        maxWidth: 400,
        minWidth: 290,
        padding: 0,
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.07))',
      }}
    >
      {/* Header with step counter */}
      <div
        style={{
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          borderRadius: '12px 12px 0 0',
          padding: '16px 20px 12px',
        }}
      >
        <div
          style={{
            color: '#64748b',
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Step {index + 1} of {size}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        <div style={{ lineHeight: 1.6, marginBottom: continuous ? 20 : 0 }}>
          {step.content}
        </div>

        {/* Action buttons */}
        {continuous && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              {index > 0 && (
                <button
                  {...backProps}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 16px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f9fafb'
                    e.target.style.borderColor = '#9ca3af'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                    e.target.style.borderColor = '#d1d5db'
                  }}
                >
                  Back
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                {...skipProps}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  padding: '8px 12px',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#6b7280'
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#9ca3af'
                }}
              >
                Skip
              </button>
              <button
                {...primaryProps}
                style={{
                  backgroundColor: isLastStep ? '#059669' : '#0ea5e9',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  padding: '8px 16px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isLastStep ? '#047857' : '#0284c7'
                  e.target.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isLastStep ? '#059669' : '#0ea5e9'
                  e.target.style.transform = 'translateY(0)'
                }}
              >
                {isLastStep ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {!continuous && (
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button
              {...closeProps}
              style={{
                backgroundColor: '#059669',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                padding: '8px 16px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#047857'
                e.target.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#059669'
                e.target.style.transform = 'translateY(0)'
              }}
            >
              Got it!
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const Tutorial = ({ run, onFinish }) => {
  const [stepIndex, setStepIndex] = useState(0)
  const navigate = useNavigate()


  // Enhanced step configuration with better targeting and content
  const steps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🎉 Welcome to EAI - Enterprise AI Evaluation Platform!
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            This tutorial will guide you through the platform. We'll start from this landing page and show you how to create test cases, run evaluations, and analyze results. Let's begin!
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: false,
    },
    {
      target: '[data-landing-hero]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🏠 EAI Landing Page
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            This is your platform's home page. From here you can learn about EAI's capabilities including precision testing, fast evaluation, and enterprise-ready features.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-landing-cta]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🚀 Ready to Get Started?
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            These buttons let you either continue this tutorial or jump straight to creating test cases. Click "Next" and we'll navigate to the Create Test Case page to continue!
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: false,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Create Test Case Page
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            You're now on the Create Test Case page. Here you can build comprehensive test cases for AI evaluation. A test case requires a title, user input, and expected responses. Let's start with the basics.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: false,
    },
    {
      target: '[data-tour="create-title"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Test Case Title
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Give your test case a descriptive title. This helps you identify and organize your test cases later. Make it specific and meaningful!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="create-user-input"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            User Input
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Enter the user's question or request exactly as they would ask it. This is what the AI will respond to during evaluation.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="create-expected-answer"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Expected Response
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Define what the ideal AI response should be. This serves as the benchmark for evaluating the AI's performance against your expectations.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="tab-presets"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Presets Tab
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Let's explore the Presets tab, which contains template configurations to speed up your test case creation.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="presets-state-selection"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Configuration Requirements
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            To manually configure, you must choose one configuration type (QA, planning pathway, document checklist, or application) and one authentication state (anonymous or logged in - referring to the test case user, not your login status).
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="presets-address-selection"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Configuration Presets Shortcut
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            You can bypass the manual configuration above by choosing a configuration preset from these council areas. Each preset includes predefined addresses and automatically sets the required configuration and authentication options.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="tab-user"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            User Configuration Tab
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Now let's move to the User Config tab, which manages user-specific settings and authentication details.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            User Configuration Tab
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            You're now in the User Config tab. Here you can import existing configurations (blue section at top) using JSON file upload, clipboard paste, or bulk upload for multiple test cases. You must also select a council and fill out all fields marked with an asterisk (*) to progress. Many fields will auto-populate from your account details.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🏢 Project Configuration Tab
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            You're now in the Project tab, which handles property details and development information.
          </p>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            <strong>⭐ Key Feature: Address Autocomplete</strong><br/>
            The main field to focus on here is the <strong>Address autocomplete field</strong>. When you enter an address and submit it, our backend APIs will automatically populate all the other project fields for you.
          </p>
          <p style={{ margin: 0, lineHeight: 1.5, color: '#6b7280', fontSize: 13 }}>
            💡 This auto-population saves you time and ensures accuracy by pulling relevant property data automatically.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="tab-policies"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Policies Configuration
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            The Policies section is there to match the User Config requirements. It's available for configuring policy-related settings as needed.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="tab-basics"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Back to Test Details
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Now let's return to the Test Details tab to complete and save your test case.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="create-save"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Save Test Case
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Once all required fields are filled, click <strong>Save Test Case</strong> to store it locally and on the backend. You can also preview and copy the JSON representation first.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: false,
    },

    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📚 Test Cases Page
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Great! You've saved your test case and now you're on the Test Cases page. This is where you can manage all your saved test cases with powerful filtering, searching, and organization features. Let's explore what you can do here.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="testcases-create-new"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            ➕ Create Test Case
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Click this button to create a new test case. You can always come back to this page to create additional test cases.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-search"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔍 Search Test Cases
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Search through your test cases by title, user input, council, address, or project ID. This helps you quickly find specific test cases.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-collection-filter"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🏷️ Collection Filter
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter test cases by collection tags. This allows you to view only test cases that belong to specific groups or categories.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-state-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🎯 State Filters
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter test cases by program state: QA, Planning Pathway, Document Checklist, or Application. Use these to focus on specific workflow stages.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-auth-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔐 Authentication Filters
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter by authentication status: Anonymous (not logged in) or Signed In users. This helps you organize test cases by user type.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-preset-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🎨 Preset Filters
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter by preset type: Anonymous, Address Only, Logged In, or Custom. These correspond to the preset configurations you can choose when creating test cases.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="testcases-selection-toolbar"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            ✅ Selection Toolbar
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Select multiple test cases to create collections or filter to your selection. This is useful for organizing and batch operations on test cases.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },

    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔬 Evaluation Runs Page
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Welcome to the Evaluation Runs page! This is where you configure and execute your test cases against AI models. You can select test cases, configure run settings, and monitor execution in real-time. Let's explore the features here.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },

    {
      target: '[data-tour="evalruns-tabs"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📑 Evaluation Tabs
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Switch between <strong>Run</strong> to configure and start new evaluations, and <strong>History</strong> to view past evaluation runs.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-search"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔍 Search Test Cases
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Search through available test cases to include in your evaluation run. Filter by title, input, council, or address.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-collections"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📚 Saved Collections
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Select from saved collections of test cases. This allows you to run evaluations on predefined sets of test cases.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-state-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🎯 State Filters
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter test cases by program state before including them in your evaluation run. Focus on specific workflow stages.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-auth-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔐 Authentication Filters
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter test cases by authentication status to include only anonymous or signed-in user scenarios in your evaluation.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-options"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            ⚙️ Run Options
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Configure your evaluation run settings: title, environment, and concurrency. These options control how your evaluation executes.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalruns-title"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📝 Run Title
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Give your evaluation run a descriptive title. This helps you identify and organize your evaluation runs in the history.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📊 Evaluation Results Page
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Now you're on the Evaluation Results page! This is where you analyze completed evaluations with detailed metrics including similarity, groundedness, relevance, and ROUGE scores. You can compare different runs and dive deep into performance analytics.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="evalresults-overview"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📊 Results Overview
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            This dashboard provides a comprehensive overview of your evaluation results. See key metrics, performance trends, and overall AI model performance at a glance.
          </p>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            The overview shows aggregate scores across all test cases, helping you quickly assess model quality and identify areas for improvement.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalresults-metrics"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📈 Performance Metrics
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            View detailed performance metrics including similarity scores, groundedness, relevance, and ROUGE scores. These metrics help you understand how well the AI model performed across different evaluation criteria.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },

    {
      target: '[data-tour="evalresults-testcases"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🧪 Individual Test Results
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Drill down into individual test case results. See exactly how the AI model performed on each specific test, including detailed response analysis and scoring breakdowns.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalresults-export"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            📤 Export Results
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Click the Export button to download your evaluation results in multiple formats: JSON (complete data), CSV (test case results), and Text Summary (overview report).
          </p>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            This allows you to analyze results in external tools, create reports, or share findings with stakeholders in their preferred format.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="evalresults-filters"]',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🔍 Results Filtering
          </h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Filter and search through your evaluation results by date, model version, test case type, or performance thresholds. This helps you focus on specific subsets of results for detailed analysis.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: 'header',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            ⚙️ Settings & Controls
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            In the top right-hand corner, you'll find your settings and controls: dark mode toggle, tutorial restart, and logout options.
          </p>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            These controls are accessible from any page and help you customize your experience and manage your account.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            🎉 Tutorial Complete!
          </h3>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            You've completed the comprehensive tour of the AI Evaluation Platform! You now know how to:
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.5 }}>
            <li>Create and manage test cases with all configuration options</li>
            <li>Use filters and search to organize your test cases</li>
            <li>Run evaluations with custom settings</li>
            <li>Analyze results with detailed metrics and dashboards</li>
            <li>Export results in multiple formats for external analysis</li>
          </ul>
          <p style={{ margin: '12px 0 0', lineHeight: 1.5, fontSize: 13, color: '#6b7280' }}>
            You can restart this tutorial anytime from the header controls. Happy evaluating! 🚀
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ], [])

  // Enhanced callback handler with better state management
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)
      setStepIndex(nextIndex)
      
      // Handle tab switching and navigation based on step progression
      const handleTabSwitching = (stepIdx) => {
        // Define which steps should trigger tab switches
        const tabSwitchMap = {
          7: 'presets',   // Step 7: Switch to presets tab
          11: 'user',     // Step 11: Switch to user config tab
          12: 'project',  // Step 12: Switch to project tab
          14: 'policies', // Step 14: Switch to policies tab
          15: 'basics',   // Step 15: Switch back to test details tab
        }
        
        // Map step numbers to page navigation targets
        const pageNavigationMap = {
          3: 'create-test-case',   // Navigate to Create Test Case page (step 3 - from landing page)
          16: 'test-cases',        // Navigate to Test Cases page when step 16 completes
          24: 'run-evaluations',   // Navigate to Evaluation Runs page when step 24 completes
          32: 'evaluation-results' // Navigate to Evaluation Results page when step 32 completes (after evalruns-title)
        }
        
        // Handle tab switches for individual steps
        const targetTab = tabSwitchMap[stepIdx]
        if (targetTab && window.setCreateTestTab) {
          console.log(`Step ${stepIdx}: Switching to tab '${targetTab}'`)
          
          // Handle all tabs the same way
          window.setCreateTestTab(targetTab).then(() => {
            console.log(`Tab ${targetTab} content rendered, tutorial can continue`);
          }).catch(err => {
            console.log(`Failed to switch to tab ${targetTab}:`, err);
          });
        }
        
        // Handle page navigation using React Router
        const targetPage = pageNavigationMap[stepIdx]
        if (targetPage) {
          console.log(`Step ${stepIdx}: Navigating to page '${targetPage}'`)
          
          // Delay navigation to prevent race conditions with joyride
          setTimeout(() => {
            switch (targetPage) {
              case 'create-test-case':
                console.log('Navigating to CreateTest page')
                navigate('/CreateTest')
                break
              case 'test-cases':
                console.log('Navigating to TestCases page')
                navigate('/TestCases')
                break
              case 'run-evaluations':
                console.log('Navigating to EvaluationRuns page')
                navigate('/EvaluationRuns')
                break
              case 'evaluation-results':
                console.log('Navigating to EvaluationResults page')
                navigate('/EvaluationResults')
                break
              default:
                console.warn(`Unknown target page: ${targetPage}`)
            }
          }, 300) // Longer delay to prevent race conditions
        }
      }
      
      // Trigger tab switching for next step
      if (action === ACTIONS.NEXT) {
        handleTabSwitching(nextIndex)
      } else if (action === ACTIONS.PREV) {
        handleTabSwitching(nextIndex)
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Reset step index and finish tutorial
      setStepIndex(0)
      if (onFinish) {
        onFinish()
      }
    }
  }, [onFinish, navigate])

  // Improved styles with better accessibility and responsive design
  const joyrideStyles = useMemo(() => ({
    options: {
      arrowColor: '#ffffff',
      backgroundColor: '#ffffff',
      overlayColor: 'rgba(0, 0, 0, 0.3)', // Lighter overlay to reduce visibility blocking
      primaryColor: '#0ea5e9',
      textColor: '#374151',
      width: undefined, // Let tooltip component handle width
      zIndex: 10000,
    },
    overlay: {
      mixBlendMode: 'normal',
      pointerEvents: 'auto', // Allow interaction with overlay
    },
    spotlight: {
      borderRadius: 8,
      backgroundColor: 'rgba(255, 255, 255, 0.1)', // Subtle spotlight
    },
    beacon: {
      outline: 'none',
    },
    beaconInner: {
      backgroundColor: '#dc2626',
    },
    beaconOuter: {
      backgroundColor: 'rgba(220, 38, 38, 0.3)',
      borderColor: 'rgba(220, 38, 38, 0.3)',
    },
  }), [])

  return (
    <>
      {/* Add custom CSS animations */}
      <style>{`
        @keyframes joyride-beacon-pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
        
        @keyframes joyride-beacon-inner {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
          100% {
            opacity: 1;
          }
        }
        
        .joyride-beacon:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        
        .joyride-tooltip {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        
        @media (max-width: 640px) {
          .joyride-tooltip {
            max-width: calc(100vw - 40px) !important;
            min-width: calc(100vw - 40px) !important;
          }
        }
        
        /* Improve visibility by reducing overlay opacity */
        [data-react-joyride-step] {
          outline: 2px solid #dc2626 !important;
          outline-offset: 4px !important;
          border-radius: 8px !important;
        }
        
        /* Ensure tooltips don't block important UI elements */
        .joyride-tooltip {
          pointer-events: auto !important;
        }
        
        /* Enhanced spotlight visibility */
        [data-react-joyride-spotlight] {
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.2) !important;
        }
      `}</style>
      
      <Joyride
        steps={steps}
        run={run}
        continuous
        showProgress
        showSkipButton
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        beaconComponent={CustomBeacon}
        tooltipComponent={CustomTooltip}
        styles={joyrideStyles}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip tour',
        }}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: {
              filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.1))',
            },
          },
        }}
        // Accessibility improvements
        disableCloseOnEsc={false}
        disableOverlayClose={false}
        hideCloseButton={false}
        // Performance optimizations  
        disableScrollParentFix={true} // Better positioning
        // Visibility improvements
        spotlightClicks={false} // Prevent accidental clicks during page loading
        disableOverlay={false} // Keep overlay but make it less intrusive
        spotlightPadding={4} // Reduce spotlight padding
        // Enhanced targeting
        debug={false}
        // Navigation improvements
        scrollToFirstStep={true}
        scrollOffset={100}
        // Better handling of missing targets during page transitions
        disableScrolling={false} // Allow scrolling to find targets after navigation
        // Add delay for page transitions
        getHelpers={false}
      />
    </>
  )
}

export default Tutorial



